import { type FunctionComponent, useCallback, useState } from 'react';
import cn from 'classnames';
import {
    HStack,
    type IconColor,
    IconCopy,
    type IconRole,
    type IconSize,
    Link,
    type LinkColor,
    type StackAlignItems,
    Text,
    type TextProps,
} from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { copyTextToClipboard } from '../../lib/string';
import styles from './TextCopy.module.css';

export interface TextCopyProps extends TestProps, Omit<TextProps, 'addonRight'> {
    copyText?: string;
    linkColor?: LinkColor;
    iconColor?: IconColor;
    iconRole?: IconRole;
    alignIcon?: StackAlignItems;
    iconSize?: IconSize;
    iconClassName?: string;
    isFullWidth?: boolean;
}

export const TextCopy: FunctionComponent<TextCopyProps> = ({
    'data-testid': dataTestId = 'TextCopy',
    children,
    copyText = typeof children === 'string' ? children : undefined,
    iconColor = 'gray',
    linkColor = 'main',
    iconRole = 'supplementary',
    alignIcon = 'center',
    iconSize = '16',
    iconClassName,
    isFullWidth = false,
    ...textProps
}) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleCopyClick = useCallback(() => {
        copyTextToClipboard(copyText);
    }, [copyText]);

    return (
        <Text data-testid={dataTestId} {...textProps} className={cn(isFullWidth && styles.full_width)}>
            <Link
                color={linkColor}
                className={cn(isFullWidth && styles.full_width)}
                onClick={handleCopyClick}
                onHoverChange={setIsHovered}
            >
                <HStack
                    gap="4"
                    alignItems={alignIcon}
                    justifyContent={isFullWidth ? 'space-between' : undefined}
                    className={cn(isFullWidth && styles.full_width)}
                >
                    <span className={cn(textProps.isTruncated && styles.truncated)}>{children}</span>
                    <IconCopy
                        className={iconClassName}
                        role={iconRole}
                        color={isHovered ? undefined : iconColor}
                        size={iconSize}
                    />
                </HStack>
            </Link>
        </Text>
    );
};
